import React, { useEffect, useMemo, useState } from "react";
import api, { dkk, formatErr } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "../components/ui/dialog";
import { Plus, Trash2, Pencil, Check, FolderPlus } from "lucide-react";
import { toast } from "sonner";

const MONTHS_DA = [
  "Jan", "Feb", "Mar", "Apr", "Maj", "Jun",
  "Jul", "Aug", "Sep", "Okt", "Nov", "Dec",
];
const MONTHS_FULL = [
  "Januar", "Februar", "Marts", "April", "Maj", "Juni",
  "Juli", "August", "September", "Oktober", "November", "December",
];

const emptyFixed = () => ({
  name: "",
  amount: 0,
  vat_deductible: false,
  months: [],
  end_date: "",
  category_id: "",
});

export default function Omkostninger() {
  return (
    <div className="p-4 sm:p-6 lg:p-10 max-w-[1400px]" data-testid="costs-page">
      <div className="overline text-zinc-500">Omkostninger</div>
      <h1 className="headline text-3xl sm:text-4xl lg:text-5xl mt-2 mb-1">Faste & variable.</h1>
      <p className="text-zinc-400 text-xs sm:text-sm mb-6 sm:mb-8">
        Generelle omkostninger på firma-niveau. Vises på Overblik.
      </p>

      <Tabs defaultValue="fixed" className="w-full">
        <TabsList className="bg-[#1F1F1F] border border-zinc-800">
          <TabsTrigger value="fixed" data-testid="tab-fixed">Faste</TabsTrigger>
          <TabsTrigger value="variable" data-testid="tab-variable">Variable</TabsTrigger>
        </TabsList>
        <TabsContent value="fixed" className="mt-6">
          <FixedCostsTab />
        </TabsContent>
        <TabsContent value="variable" className="mt-6">
          <VariableCostsTab />
        </TabsContent>
      </Tabs>
    </div>
  );
}

// -----------------------------------------------------------------------------
// FASTE
// -----------------------------------------------------------------------------
function FixedCostsTab() {
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null | "new" | id
  const [form, setForm] = useState(emptyFixed());
  const [showCatDialog, setShowCatDialog] = useState(false);
  const [catName, setCatName] = useState("");
  const [editCat, setEditCat] = useState(null); // null | id
  const [presetCategoryId, setPresetCategoryId] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const [r1, r2] = await Promise.all([
        api.get("/costs/fixed"),
        api.get("/costs/categories"),
      ]);
      setItems(r1.data || []);
      setCategories(r2.data || []);
    } catch (e) {
      toast.error(formatErr(e));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const openNew = (categoryId = "") => {
    setForm({ ...emptyFixed(), category_id: categoryId });
    setPresetCategoryId(categoryId);
    setEditing("new");
  };
  const openEdit = (it) => {
    setForm({
      name: it.name || "",
      amount: it.amount || 0,
      vat_deductible: !!it.vat_deductible,
      months: [...(it.months || [])],
      end_date: it.end_date || "",
      category_id: it.category_id || "",
    });
    setEditing(it.id);
  };

  const save = async () => {
    if (!form.name.trim()) {
      toast.error("Navn skal udfyldes");
      return;
    }
    const payload = {
      name: form.name.trim(),
      amount: Number(form.amount) || 0,
      vat_deductible: form.vat_deductible,
      months: form.months,
      end_date: form.end_date || null,
      category_id: form.category_id || null,
    };
    try {
      if (editing === "new") {
        await api.post("/costs/fixed", payload);
        toast.success("Omkostning oprettet");
      } else {
        await api.patch(`/costs/fixed/${editing}`, payload);
        toast.success("Omkostning opdateret");
      }
      setEditing(null);
      load();
    } catch (e) {
      toast.error(formatErr(e));
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Slet denne omkostning?")) return;
    try {
      await api.delete(`/costs/fixed/${id}`);
      toast.success("Slettet");
      load();
    } catch (e) {
      toast.error(formatErr(e));
    }
  };

  const toggleMonth = (m) => {
    setForm((f) => ({
      ...f,
      months: f.months.includes(m) ? f.months.filter((x) => x !== m) : [...f.months, m].sort((a, b) => a - b),
    }));
  };

  const saveCategory = async () => {
    if (!catName.trim()) { toast.error("Navn skal udfyldes"); return; }
    try {
      if (editCat) {
        await api.patch(`/costs/categories/${editCat}`, { name: catName.trim() });
        toast.success("Liste opdateret");
      } else {
        await api.post("/costs/categories", { name: catName.trim() });
        toast.success("Liste oprettet");
      }
      setShowCatDialog(false);
      setCatName("");
      setEditCat(null);
      load();
    } catch (e) {
      toast.error(formatErr(e));
    }
  };

  const deleteCategory = async (id) => {
    if (!window.confirm("Slet denne liste? Omkostningerne bliver flyttet til 'Uden liste'.")) return;
    try {
      await api.delete(`/costs/categories/${id}`);
      toast.success("Liste slettet");
      load();
    } catch (e) {
      toast.error(formatErr(e));
    }
  };

  const totalYear = useMemo(() => {
    return items.reduce((sum, c) => sum + (c.months?.length || 0) * Number(c.amount || 0), 0);
  }, [items]);

  // Group items by category
  const grouped = useMemo(() => {
    const map = {};
    for (const cat of categories) map[cat.id] = [];
    map["__none__"] = [];
    for (const it of items) {
      const key = it.category_id && map[it.category_id] !== undefined ? it.category_id : "__none__";
      map[key].push(it);
    }
    return map;
  }, [items, categories]);

  return (
    <div data-testid="fixed-costs-tab">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <div className="text-sm text-zinc-400">
          Total i år (uden slut-dato hensyn): <span className="text-white font-medium">{dkk(totalYear)}</span>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={() => { setEditCat(null); setCatName(""); setShowCatDialog(true); }}
            className="border-zinc-700 bg-transparent text-zinc-200 hover:bg-[#262626]"
            data-testid="btn-add-category"
          >
            <FolderPlus className="w-4 h-4 mr-1" /> Ny liste
          </Button>
          <Button
            onClick={() => openNew("")}
            className="bg-[#C8A661] hover:bg-[#B89651] text-black"
            data-testid="btn-add-fixed-cost"
          >
            <Plus className="w-4 h-4 mr-1" /> Tilføj
          </Button>
        </div>
      </div>

      {loading && <div className="section-card p-10 text-center text-zinc-500">Indlæser…</div>}

      {!loading && (
        <div className="space-y-6" data-testid="fixed-costs-list">
          {categories.map((cat) => (
            <CategoryGroup
              key={cat.id}
              category={cat}
              items={grouped[cat.id] || []}
              onAdd={() => openNew(cat.id)}
              onEdit={openEdit}
              onDelete={remove}
              onRenameCat={() => { setEditCat(cat.id); setCatName(cat.name); setShowCatDialog(true); }}
              onDeleteCat={() => deleteCategory(cat.id)}
            />
          ))}
          <CategoryGroup
            category={{ id: "__none__", name: "Uden liste" }}
            items={grouped["__none__"] || []}
            onAdd={() => openNew("")}
            onEdit={openEdit}
            onDelete={remove}
          />
          {items.length === 0 && categories.length === 0 && (
            <div className="section-card p-10 text-center text-zinc-500">
              Ingen lister eller omkostninger endnu. Opret en liste eller tilføj direkte.
            </div>
          )}
        </div>
      )}

      <Dialog open={editing !== null} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="bg-[#1A1A1A] border-zinc-800 text-white max-w-lg" data-testid="fixed-cost-dialog">
          <DialogHeader>
            <DialogTitle>{editing === "new" ? "Ny fast omkostning" : "Rediger omkostning"}</DialogTitle>
            <DialogDescription className="text-zinc-500 text-xs">
              Vælg de måneder hvor omkostningen gælder. Slut-dato er valgfri.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-zinc-400">Navn</Label>
              <Input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="fx Internet, Bogføring, ..."
                className="bg-[#262626] border-zinc-700"
                data-testid="input-fixed-name"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-zinc-400">Beløb pr. måned (DKK)</Label>
                <Input
                  type="number"
                  value={form.amount}
                  onChange={(e) => setForm({ ...form, amount: e.target.value })}
                  className="bg-[#262626] border-zinc-700"
                  data-testid="input-fixed-amount"
                />
              </div>
              <div>
                <Label className="text-zinc-400">Liste</Label>
                <select
                  value={form.category_id || ""}
                  onChange={(e) => setForm({ ...form, category_id: e.target.value })}
                  className="w-full bg-[#262626] border border-zinc-700 text-white text-sm rounded px-3 py-2 h-10"
                  data-testid="select-fixed-category"
                >
                  <option value="">— Uden liste —</option>
                  {categories.map((c) => (
                    <option key={c.id} value={c.id}>{c.name}</option>
                  ))}
                </select>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Switch
                checked={form.vat_deductible}
                onCheckedChange={(v) => setForm({ ...form, vat_deductible: v })}
                data-testid="switch-fixed-vat"
              />
              <Label className="text-zinc-300">Moms-fradrag</Label>
            </div>
            <div>
              <Label className="text-zinc-400 mb-2 block">Vælg måneder</Label>
              <div className="grid grid-cols-4 gap-2">
                {MONTHS_DA.map((label, idx) => {
                  const m = idx + 1;
                  const on = form.months.includes(m);
                  return (
                    <button
                      key={m}
                      type="button"
                      onClick={() => toggleMonth(m)}
                      className={
                        "px-3 py-2 rounded text-xs border transition-colors " +
                        (on
                          ? "bg-[#C8A661] border-[#C8A661] text-black font-medium"
                          : "bg-[#262626] border-zinc-700 text-zinc-300 hover:border-zinc-500")
                      }
                      data-testid={`month-toggle-${m}`}
                    >
                      {on && <Check className="w-3 h-3 inline mr-1" />}
                      {label}
                    </button>
                  );
                })}
              </div>
              <div className="flex gap-2 mt-2">
                <button
                  type="button"
                  onClick={() => setForm({ ...form, months: [1,2,3,4,5,6,7,8,9,10,11,12] })}
                  className="text-xs text-zinc-400 hover:text-white"
                  data-testid="btn-select-all-months"
                >
                  Vælg alle
                </button>
                <span className="text-zinc-700">·</span>
                <button
                  type="button"
                  onClick={() => setForm({ ...form, months: [] })}
                  className="text-xs text-zinc-400 hover:text-white"
                  data-testid="btn-clear-months"
                >
                  Ryd
                </button>
              </div>
            </div>
            <div>
              <Label className="text-zinc-400">Slut-dato (valgfri)</Label>
              <Input
                type="date"
                value={form.end_date}
                onChange={(e) => setForm({ ...form, end_date: e.target.value })}
                className="bg-[#262626] border-zinc-700"
                data-testid="input-fixed-end-date"
              />
              <p className="text-xs text-zinc-500 mt-1">Måneder efter denne dato medregnes ikke.</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)} className="border-zinc-700 bg-transparent">Annullér</Button>
            <Button
              onClick={save}
              className="bg-[#C8A661] hover:bg-[#B89651] text-black"
              data-testid="btn-save-fixed-cost"
            >
              Gem
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={showCatDialog} onOpenChange={(o) => { if (!o) { setShowCatDialog(false); setEditCat(null); setCatName(""); } }}>
        <DialogContent className="bg-[#1A1A1A] border-zinc-800 text-white max-w-md" data-testid="category-dialog">
          <DialogHeader>
            <DialogTitle>{editCat ? "Omdøb liste" : "Ny liste"}</DialogTitle>
            <DialogDescription className="text-zinc-500 text-xs">
              Lister bruges til at gruppere faste omkostninger.
            </DialogDescription>
          </DialogHeader>
          <div>
            <Label className="text-zinc-400">Navn</Label>
            <Input
              value={catName}
              onChange={(e) => setCatName(e.target.value)}
              placeholder="fx IT/mobil abonnementer, Lokaleomkostninger, ..."
              className="bg-[#262626] border-zinc-700"
              data-testid="input-category-name"
              autoFocus
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowCatDialog(false); setEditCat(null); setCatName(""); }} className="border-zinc-700 bg-transparent">Annullér</Button>
            <Button onClick={saveCategory} className="bg-[#C8A661] hover:bg-[#B89651] text-black" data-testid="btn-save-category">
              Gem
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function CategoryGroup({ category, items, onAdd, onEdit, onDelete, onRenameCat, onDeleteCat }) {
  const total = items.reduce((s, it) => s + (it.months?.length || 0) * Number(it.amount || 0), 0);
  const isNoneList = category.id === "__none__";
  // Hide "Uden liste" entirely when empty
  if (isNoneList && items.length === 0) return null;
  return (
    <div className="section-card overflow-x-auto" data-testid={`category-${category.id}`}>
      <div className="flex items-center justify-between px-4 py-3 bg-[#1F1F1F] border-b border-zinc-800">
        <div className="flex items-center gap-3">
          <div className="text-sm font-medium text-white">{category.name}</div>
          <div className="text-xs text-zinc-500">
            {items.length} {items.length === 1 ? "post" : "poster"} · {dkk(total)} / år
          </div>
        </div>
        <div className="flex items-center gap-1">
          <button
            onClick={onAdd}
            className="text-xs text-[#C8A661] hover:text-[#E0BC75] px-2 py-1"
            data-testid={`btn-add-in-${category.id}`}
          >
            + Tilføj
          </button>
          {!isNoneList && (
            <>
              <button
                onClick={onRenameCat}
                className="text-zinc-500 hover:text-white p-1"
                data-testid={`btn-rename-cat-${category.id}`}
              >
                <Pencil className="w-3.5 h-3.5" />
              </button>
              <button
                onClick={onDeleteCat}
                className="text-zinc-500 hover:text-red-400 p-1"
                data-testid={`btn-delete-cat-${category.id}`}
              >
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </>
          )}
        </div>
      </div>
      {items.length === 0 ? (
        <div className="px-4 py-6 text-center text-zinc-500 text-xs">
          Ingen omkostninger i denne liste endnu.
        </div>
      ) : (
        <table className="w-full text-sm min-w-[600px]">
          <thead className="text-zinc-500 text-xs uppercase tracking-wider">
            <tr className="border-b border-zinc-800">
              <th className="text-left px-4 py-2">Navn</th>
              <th className="text-right px-4 py-2">Beløb / md</th>
              <th className="text-center px-4 py-2">Moms</th>
              <th className="text-left px-4 py-2">Måneder</th>
              <th className="text-left px-4 py-2">Slut-dato</th>
              <th className="text-right px-4 py-2"></th>
            </tr>
          </thead>
          <tbody>
            {items.map((it) => (
              <tr key={it.id} className="border-t border-zinc-800 hover:bg-[#1F1F1F]">
                <td className="px-4 py-3 text-white">{it.name}</td>
                <td className="px-4 py-3 text-right text-white">{dkk(it.amount)}</td>
                <td className="px-4 py-3 text-center">
                  {it.vat_deductible ? (
                    <span className="text-xs text-emerald-400">Fradrag</span>
                  ) : (
                    <span className="text-xs text-zinc-500">—</span>
                  )}
                </td>
                <td className="px-4 py-3">
                  <div className="flex flex-wrap gap-1">
                    {(it.months || []).map((m) => (
                      <span key={m} className="text-[10px] px-1.5 py-0.5 rounded bg-[#262626] text-zinc-300 border border-zinc-700">
                        {MONTHS_DA[m - 1]}
                      </span>
                    ))}
                    {(!it.months || it.months.length === 0) && (
                      <span className="text-xs text-zinc-500">—</span>
                    )}
                  </div>
                </td>
                <td className="px-4 py-3 text-zinc-400 text-xs">
                  {it.end_date || <span className="text-zinc-600">—</span>}
                </td>
                <td className="px-4 py-3 text-right">
                  <button
                    onClick={() => onEdit(it)}
                    className="text-zinc-400 hover:text-white p-1.5"
                    data-testid={`btn-edit-fixed-${it.id}`}
                  >
                    <Pencil className="w-3.5 h-3.5" />
                  </button>
                  <button
                    onClick={() => onDelete(it.id)}
                    className="text-zinc-400 hover:text-red-400 p-1.5 ml-1"
                    data-testid={`btn-delete-fixed-${it.id}`}
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

// -----------------------------------------------------------------------------
// VARIABLE
// -----------------------------------------------------------------------------
function VariableCostsTab() {
  const currentYear = new Date().getFullYear();
  const [year, setYear] = useState(currentYear);
  const [vendors, setVendors] = useState([]);
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [vendorDialog, setVendorDialog] = useState(null); // null | "new" | vendor object
  const [vendorForm, setVendorForm] = useState({ name: "", vat_deductible: false, vendor_number: "" });
  const [view, setView] = useState("table"); // table | month
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);

  const load = async () => {
    setLoading(true);
    try {
      const [vendorsRes, entriesRes] = await Promise.all([
        api.get("/costs/vendors"),
        api.get(`/costs/variable?year=${year}`),
      ]);
      setVendors(vendorsRes.data || []);
      setEntries(entriesRes.data || []);
    } catch (e) {
      toast.error(formatErr(e));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, [year]);  // eslint-disable-line

  const entryMap = useMemo(() => {
    const m = {};
    for (const e of entries) m[`${e.vendor_id}|${e.year_month}`] = e;
    return m;
  }, [entries]);

  const upsertEntry = async (vendor_id, year_month, amount) => {
    try {
      const num = Number(amount) || 0;
      await api.put("/costs/variable", { vendor_id, year_month, amount: num });
      setEntries((prev) => {
        const key = `${vendor_id}|${year_month}`;
        const others = prev.filter((e) => `${e.vendor_id}|${e.year_month}` !== key);
        if (num > 0) {
          return [...others, { vendor_id, year_month, amount: num, id: prev.find((e) => `${e.vendor_id}|${e.year_month}` === key)?.id || "tmp" }];
        }
        return others;
      });
    } catch (e) {
      toast.error(formatErr(e));
    }
  };

  const openNewVendor = () => {
    setVendorForm({ name: "", vat_deductible: false, vendor_number: "" });
    setVendorDialog("new");
  };
  const openEditVendor = (v) => {
    setVendorForm({
      name: v.name || "",
      vat_deductible: !!v.vat_deductible,
      vendor_number: v.vendor_number || "",
    });
    setVendorDialog(v);
  };

  const saveVendor = async () => {
    if (!vendorForm.name.trim()) {
      toast.error("Navn skal udfyldes");
      return;
    }
    if (!vendorForm.vendor_number.trim()) {
      toast.error("Leverandørnummer skal udfyldes");
      return;
    }
    const payload = {
      name: vendorForm.name.trim(),
      vat_deductible: vendorForm.vat_deductible,
      vendor_number: vendorForm.vendor_number.trim(),
    };
    try {
      if (vendorDialog === "new") {
        await api.post("/costs/vendors", payload);
        toast.success("Leverandør tilføjet");
      } else {
        await api.patch(`/costs/vendors/${vendorDialog.id}`, payload);
        toast.success("Leverandør opdateret");
      }
      setVendorDialog(null);
      load();
    } catch (e) {
      toast.error(formatErr(e));
    }
  };

  const deleteVendor = async (id) => {
    if (!window.confirm("Slet leverandør? Alle posteringer slettes også.")) return;
    try {
      await api.delete(`/costs/vendors/${id}`);
      toast.success("Slettet");
      load();
    } catch (e) {
      toast.error(formatErr(e));
    }
  };

  const toggleVendorVat = async (v) => {
    try {
      await api.patch(`/costs/vendors/${v.id}`, { vat_deductible: !v.vat_deductible });
      load();
    } catch (e) {
      toast.error(formatErr(e));
    }
  };

  const totalYear = useMemo(() => entries.reduce((s, e) => s + Number(e.amount || 0), 0), [entries]);
  const monthTotal = (m) =>
    entries
      .filter((e) => e.year_month === `${year}-${String(m).padStart(2, "0")}`)
      .reduce((s, e) => s + Number(e.amount || 0), 0);

  return (
    <div data-testid="variable-costs-tab">
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <select
          value={year}
          onChange={(e) => setYear(Number(e.target.value))}
          className="bg-[#262626] border border-zinc-700 text-white text-sm rounded px-3 py-2"
          data-testid="select-year"
        >
          {[currentYear - 1, currentYear, currentYear + 1].map((y) => (
            <option key={y} value={y}>{y}</option>
          ))}
        </select>
        <div className="flex bg-[#1F1F1F] border border-zinc-800 rounded p-0.5">
          <button
            onClick={() => setView("table")}
            className={"px-3 py-1.5 text-xs rounded " + (view === "table" ? "bg-[#C8A661] text-black" : "text-zinc-400")}
            data-testid="view-table"
          >
            Tabel
          </button>
          <button
            onClick={() => setView("month")}
            className={"px-3 py-1.5 text-xs rounded " + (view === "month" ? "bg-[#C8A661] text-black" : "text-zinc-400")}
            data-testid="view-month"
          >
            Måned
          </button>
        </div>
        <div className="ml-auto text-sm text-zinc-400">
          Total {year}: <span className="text-white font-medium">{dkk(totalYear)}</span>
        </div>
        <Button
          onClick={openNewVendor}
          className="bg-[#C8A661] hover:bg-[#B89651] text-black"
          data-testid="btn-add-vendor"
        >
          <Plus className="w-4 h-4 mr-1" /> Leverandør
        </Button>
      </div>

      {loading ? (
        <div className="section-card p-10 text-center text-zinc-500">Indlæser…</div>
      ) : vendors.length === 0 ? (
        <div className="section-card p-10 text-center text-zinc-500">
          Ingen leverandører endnu. Tilføj en for at komme i gang.
        </div>
      ) : view === "table" ? (
        <TableView
          year={year}
          vendors={vendors}
          entryMap={entryMap}
          onUpsert={upsertEntry}
          onEditVendor={openEditVendor}
          onDeleteVendor={deleteVendor}
          onToggleVat={toggleVendorVat}
          monthTotal={monthTotal}
        />
      ) : (
        <MonthView
          year={year}
          month={selectedMonth}
          onMonthChange={setSelectedMonth}
          vendors={vendors}
          entryMap={entryMap}
          onUpsert={upsertEntry}
        />
      )}

      <Dialog open={vendorDialog !== null} onOpenChange={(o) => !o && setVendorDialog(null)}>
        <DialogContent className="bg-[#1A1A1A] border-zinc-800 text-white max-w-md" data-testid="vendor-dialog">
          <DialogHeader>
            <DialogTitle>{vendorDialog === "new" ? "Ny leverandør" : "Rediger leverandør"}</DialogTitle>
            <DialogDescription className="text-zinc-500 text-xs">
              Tilføj navn og leverandørnummer. Marker moms-fradrag hvis relevant.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-zinc-400">Navn</Label>
              <Input
                value={vendorForm.name}
                onChange={(e) => setVendorForm({ ...vendorForm, name: e.target.value })}
                placeholder="fx Brændstof, Vask, ..."
                className="bg-[#262626] border-zinc-700"
                data-testid="input-vendor-name"
              />
            </div>
            <div>
              <Label className="text-zinc-400">Leverandørnummer</Label>
              <Input
                value={vendorForm.vendor_number}
                onChange={(e) => setVendorForm({ ...vendorForm, vendor_number: e.target.value })}
                placeholder="fx 1001"
                className="bg-[#262626] border-zinc-700"
                data-testid="input-vendor-number"
              />
            </div>
            <div className="flex items-center gap-3">
              <Switch
                checked={vendorForm.vat_deductible}
                onCheckedChange={(v) => setVendorForm({ ...vendorForm, vat_deductible: v })}
                data-testid="switch-vendor-vat"
              />
              <Label className="text-zinc-300">Moms-fradrag</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setVendorDialog(null)} className="border-zinc-700 bg-transparent">Annullér</Button>
            <Button onClick={saveVendor} className="bg-[#C8A661] hover:bg-[#B89651] text-black" data-testid="btn-save-vendor">
              Gem
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function TableView({ year, vendors, entryMap, onUpsert, onEditVendor, onDeleteVendor, onToggleVat, monthTotal }) {
  return (
    <div className="section-card overflow-x-auto" data-testid="variable-table">
      <table className="w-full text-sm min-w-[900px]">
        <thead className="bg-[#1F1F1F] text-zinc-500 text-xs uppercase tracking-wider">
          <tr>
            <th className="text-left px-3 py-3 sticky left-0 bg-[#1F1F1F] z-10">Leverandør</th>
            {MONTHS_DA.map((m) => (
              <th key={m} className="text-right px-2 py-3 w-[78px]">{m}</th>
            ))}
            <th className="text-right px-3 py-3">Total</th>
            <th className="px-2 py-3"></th>
          </tr>
        </thead>
        <tbody>
          {vendors.map((v) => {
            const total = MONTHS_DA.reduce((s, _, i) => {
              const ym = `${year}-${String(i + 1).padStart(2, "0")}`;
              return s + Number(entryMap[`${v.id}|${ym}`]?.amount || 0);
            }, 0);
            return (
              <tr key={v.id} className="border-t border-zinc-800">
                <td className="px-3 py-2 sticky left-0 bg-[#1A1A1A] z-10">
                  <div className="flex items-center gap-2">
                    <div className="min-w-0">
                      <div className="text-white truncate">{v.name}</div>
                      <div className="text-[10px] text-zinc-500">
                        {v.vendor_number ? `# ${v.vendor_number}` : <span className="text-amber-500">Mangler nr.</span>}
                      </div>
                    </div>
                    <button
                      onClick={() => onToggleVat(v)}
                      className={"text-[10px] px-1.5 py-0.5 rounded border " + (v.vat_deductible ? "border-emerald-700 text-emerald-400" : "border-zinc-700 text-zinc-500")}
                      title="Moms-fradrag"
                      data-testid={`vendor-vat-${v.id}`}
                    >
                      {v.vat_deductible ? "Moms" : "—"}
                    </button>
                  </div>
                </td>
                {MONTHS_DA.map((_, i) => {
                  const ym = `${year}-${String(i + 1).padStart(2, "0")}`;
                  const val = entryMap[`${v.id}|${ym}`]?.amount;
                  return (
                    <td key={i} className="px-1 py-1">
                      <CellInput
                        value={val}
                        onCommit={(n) => onUpsert(v.id, ym, n)}
                        testid={`cell-${v.id}-${i + 1}`}
                      />
                    </td>
                  );
                })}
                <td className="px-3 py-2 text-right text-white font-medium">{total ? dkk(total) : <span className="text-zinc-600">—</span>}</td>
                <td className="px-2 py-2 text-right whitespace-nowrap">
                  <button
                    onClick={() => onEditVendor(v)}
                    className="text-zinc-500 hover:text-white p-1"
                    data-testid={`btn-edit-vendor-${v.id}`}
                    title="Rediger leverandør"
                  >
                    <Pencil className="w-3.5 h-3.5" />
                  </button>
                  <button
                    onClick={() => onDeleteVendor(v.id)}
                    className="text-zinc-500 hover:text-red-400 p-1 ml-1"
                    data-testid={`btn-delete-vendor-${v.id}`}
                    title="Slet leverandør"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </td>
              </tr>
            );
          })}
          <tr className="border-t border-zinc-800 bg-[#1F1F1F]">
            <td className="px-3 py-2 sticky left-0 bg-[#1F1F1F] text-zinc-500 text-xs uppercase tracking-wider">Total</td>
            {MONTHS_DA.map((_, i) => {
              const t = monthTotal(i + 1);
              return (
                <td key={i} className="px-2 py-2 text-right text-zinc-300 text-xs">
                  {t ? dkk(t) : <span className="text-zinc-700">—</span>}
                </td>
              );
            })}
            <td colSpan={2} />
          </tr>
        </tbody>
      </table>
    </div>
  );
}

function CellInput({ value, onCommit, testid }) {
  const [v, setV] = useState(value ?? "");
  useEffect(() => { setV(value ?? ""); }, [value]);
  const commit = () => {
    const n = Number(v) || 0;
    if (n !== Number(value || 0)) onCommit(n);
  };
  return (
    <input
      type="number"
      value={v}
      onChange={(e) => setV(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => { if (e.key === "Enter") e.target.blur(); }}
      className="w-full bg-transparent border border-transparent hover:border-zinc-700 focus:border-[#C8A661] focus:outline-none text-right text-white text-xs px-1.5 py-1 rounded"
      placeholder="—"
      data-testid={testid}
    />
  );
}

function MonthView({ year, month, onMonthChange, vendors, entryMap, onUpsert }) {
  return (
    <div className="section-card p-6" data-testid="variable-month-view">
      <div className="flex items-center gap-3 mb-5 flex-wrap">
        <Label className="text-zinc-400">Måned:</Label>
        <select
          value={month}
          onChange={(e) => onMonthChange(Number(e.target.value))}
          className="bg-[#262626] border border-zinc-700 text-white text-sm rounded px-3 py-2"
          data-testid="select-month"
        >
          {MONTHS_FULL.map((m, i) => (
            <option key={m} value={i + 1}>{m}</option>
          ))}
        </select>
      </div>
      <div className="space-y-2">
        {vendors.map((v) => {
          const ym = `${year}-${String(month).padStart(2, "0")}`;
          const val = entryMap[`${v.id}|${ym}`]?.amount;
          return (
            <div key={v.id} className="flex items-center gap-3 p-3 bg-[#1F1F1F] rounded border border-zinc-800">
              <div className="flex-1">
                <div className="text-white text-sm">{v.name}</div>
                <div className="flex items-center gap-2 mt-0.5">
                  <span className="text-[10px] text-zinc-500">
                    {v.vendor_number ? `# ${v.vendor_number}` : <span className="text-amber-500">Mangler nr.</span>}
                  </span>
                  {v.vat_deductible && <span className="text-[10px] text-emerald-400 uppercase tracking-wider">Moms</span>}
                </div>
              </div>
              <div className="w-40">
                <CellInput
                  value={val}
                  onCommit={(n) => onUpsert(v.id, ym, n)}
                  testid={`month-cell-${v.id}`}
                />
              </div>
              <span className="text-xs text-zinc-500 w-10">DKK</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}
